/**
 * Appointment-date resolution for the Dispatch Planner's dispatch payload.
 *
 * Every dispatched leg sends BOTH `origapptdate` and `destapptdate`, including
 * sides whose value didn't change: omitting a side that already has a value
 * makes CMS treat the partial payload as a date change against current state
 * and reject it ("destination appointment has been set to another date").
 * Echoing the existing value lets unchanged dates pass through cleanly.
 *
 * Extracted from DispatchPlanner so the precedence rules are unit-testable —
 * they've drawn bugs before (the 00/00 sentinel, the partial-payload
 * rejection, and an override that silently didn't override).
 */

/** The subset of an OrderLeg this module reads. */
export interface ApptDateLeg {
  legnum: number;
  origApptDate?: string;
  destApptDate?: string;
}

export type ApptSide = 'orig' | 'dest';

/**
 * Blank-date sentinels the backend can emit. Beyond empty/dash, `SHARED_ORDER_FIELDS`
 * guards `l.origapptdate = 0 OR IS NULL` — but a literal '0000-00-00' column value
 * slips past that guard and reaches DATE_FORMAT, which renders it as MM/DD-style
 * zeros ('00/00' or '00/00/00'). Without the zero-MM/DD case those look filled and
 * skip the autofill, even though CMS still rejects the dispatch as "no orig appt set".
 */
export const isMissingApptDate = (value: string | null | undefined): boolean => {
  if (!value) return true;
  const s = value.trim();
  if (!s || s === '-' || s === '0000-00-00') return true;
  if (/^0+\/0+(\/0+)?$/.test(s)) return true;
  return false;
};

/**
 * Convert a leg's display-format appt date (MM/DD or MM/DD/YY, as produced by
 * SHARED_ORDER_FIELDS in orders.py) into YYYY-MM-DD for the dispatch payload.
 * MM/DD assumes the current year — matching how the backend itself formats
 * same-year dates. Returns null on unparseable input so callers drop the field
 * rather than send garbage.
 */
export const legDateToIso = (
  input: string | null | undefined,
  now: Date = new Date(),
): string | null => {
  const s = (input ?? '').trim();
  if (!s || s === '-' || s === '0000-00-00') return null;
  const parts = s.split('/');
  if (parts.length === 2) {
    const [m, d] = parts;
    if (!m || !d) return null;
    return `${now.getFullYear()}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  if (parts.length === 3) {
    const [m, d, y] = parts;
    if (!m || !d || !y) return null;
    const year = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
    if (!Number.isFinite(year)) return null;
    return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
};

/**
 * Pick a fill date for a missing orig/dest appt by inspecting the rest of the
 * dispatch group. Priority: the other side of the same leg (orig ↔ dest
 * mirror) → any non-empty date elsewhere in the group. NEVER falls back to
 * today — imports/exports always have at least one date set in the group, so a
 * today-fallback would fabricate a date rather than surface the rare
 * "no date anywhere" case.
 */
export const pickFillFromScope = (
  targetLeg: ApptDateLeg,
  scopeLegs: ApptDateLeg[],
  side: ApptSide,
  now: Date = new Date(),
): string | null => {
  const otherField = side === 'orig' ? 'destApptDate' : 'origApptDate';
  const otherVal = (targetLeg[otherField] ?? '').trim();
  if (otherVal) return legDateToIso(otherVal, now);
  for (const l of scopeLegs) {
    if (l.legnum === targetLeg.legnum) continue;
    const orig = (l.origApptDate ?? '').trim();
    if (orig) return legDateToIso(orig, now);
    const dest = (l.destApptDate ?? '').trim();
    if (dest) return legDateToIso(dest, now);
  }
  return null;
};

/**
 * Normalize an order-level `appointmentDate` into an ISO date the date picker
 * can seed from, or null when there's no real date.
 *
 * `appointmentDate` is not a column — it's the `apptdate_order` SQL expression
 * (`IF((legnum = originateleg OR otr='Y') AND origapptdate != 0, origapptdate,
 * destapptdate)`), so it arrives as a raw date. On the Drop Released board an
 * undated leg comes back as the literal string '0000-00-00' rather than null
 * (verified against prod 2026-09-02 — 0 of 595 rows returned null), so the
 * zero-date sentinel has to be rejected explicitly or the picker would try to
 * render year 0.
 */
export const apptDateToIso = (value: string | null | undefined): string | null => {
  const s = (value ?? '').trim();
  if (!s) return null;
  const datePart = s.split(' ')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const [y, m, d] = datePart.split('-');
  if (y === '0000' || m === '00' || d === '00') return null;
  return datePart;
};

export interface ResolveApptDatesArgs {
  /** The leg being sent. */
  leg: ApptDateLeg;
  /** Every leg in the leg's dispatch group (i.e. `order.legs`). */
  groupLegs: ApptDateLeg[];
  /** True when this row came from the Drop Released board. */
  isDropReleased: boolean;
  /**
   * An explicit user-picked date (ISO) for this row, or null/undefined when the
   * user didn't touch the cell. When set it OVERRIDES both sides of every leg
   * in the dispatch group EXCEPT legs at or below `deliveryLeg` — pass the same
   * value for each leg of the group and let this function do the skipping.
   */
  overrideIso?: string | null;
  /**
   * The order's delivery leg (`o.deliveryleg`). Legs at or below it keep their
   * existing dates even under an override, because those are CUSTOMER
   * appointments the dispatcher doesn't own.
   *
   * This is a structural guard, not a belt-and-braces one. The listing only
   * surfaces legs past `o.deliveryleg`, but `_augment_orders_with_linked_legs`
   * then pulls in every sibling in the same dispatch group — and a group only
   * breaks AFTER a `legdrop='Y'` leg, so a live-load delivery leg (`legdrop='N'`)
   * sits in the SAME group as the drop-released leg that follows it. Prod
   * 2026-09-02: PROs 5109198 / 5109281 are exactly that shape — leg 1
   * PICKUP->DELIVERY, legdrop='N', a real 2026-09-02 appointment, deliveryleg=1,
   * with the drop-released leg 2 behind it. They're held off the board today
   * only by the EXPORT `firstrcvdate <= CURDATE()` gate (5109281 clears it
   * tomorrow), and IMPORT has no equivalent gate. Without this skip a pick would
   * overwrite that appointment and fire leg_deliverby's customer EDI cascade.
   *
   * Omit/null to apply the override to every leg (no delivery leg known).
   */
  deliveryLeg?: number | null;
  /** Today as YYYY-MM-DD — the Drop Released fallback for a blank side. */
  todayIso: string;
  /** Injectable clock for the MM/DD current-year assumption. */
  now?: Date;
}

/**
 * Resolve the `origapptdate` / `destapptdate` a single leg should send.
 *
 * Precedence per side:
 *   1. `overrideIso` — an explicit user pick wins over everything, including a
 *      date the leg already has. This is what makes the Drop Released date
 *      cell an edit rather than a fill-the-blanks. Skipped for legs at or below
 *      `deliveryLeg`, whose dates are customer appointments (see the field docs).
 *   2. The leg's own existing date, echoed back unchanged.
 *   3. Drop Released: today. Imports/Exports: any date elsewhere in the group.
 *
 * A side resolving to null is omitted by the caller rather than sent empty.
 *
 * When the override actually applied, the result also carries `apptoverride: 1`.
 * CMS refuses a dispatch that carries a DIFFERENT appointment date than the leg
 * already holds ("Cannot update, destination appointment has been set to another
 * date", cmsapi.pl updateLegDataList) — a stale-board concurrency check that
 * predates this cell being editable, and the reason a planner pick silently
 * bounced red on exactly the pros that already had a termination appointment.
 * The flag tells CMS this change is deliberate. It rides ONLY on legs the
 * override really touched, so a customer appointment on a delivery leg — which
 * keeps its existing date above — can never carry it.
 */
export function resolveLegApptDates({
  leg,
  groupLegs,
  isDropReleased,
  overrideIso,
  deliveryLeg,
  todayIso,
  now = new Date(),
}: ResolveApptDatesArgs): {
  origapptdate?: string;
  destapptdate?: string;
  apptoverride?: 1;
} {
  // A dispatch group can reach back to include the delivery leg; its dates are
  // the customer's, not the dispatcher's, so an override must not touch them.
  const isCustomerLeg =
    deliveryLeg != null &&
    Number.isFinite(Number(deliveryLeg)) &&
    Number(leg.legnum) <= Number(deliveryLeg);
  const effectiveOverride = isCustomerLeg ? null : overrideIso;

  const resolveSide = (side: ApptSide): string | null => {
    if (effectiveOverride) return effectiveOverride;
    const existing = side === 'orig' ? leg.origApptDate : leg.destApptDate;
    if (!isMissingApptDate(existing)) return legDateToIso(existing, now);
    if (isDropReleased) return todayIso;
    return pickFillFromScope(leg, groupLegs, side, now);
  };

  const out: { origapptdate?: string; destapptdate?: string; apptoverride?: 1 } = {};
  const orig = resolveSide('orig');
  if (orig) out.origapptdate = orig;
  const dest = resolveSide('dest');
  if (dest) out.destapptdate = dest;
  // Only when this leg's dates actually came from the pick.
  if (effectiveOverride && (orig || dest)) out.apptoverride = 1;
  return out;
}
